/**
 * OpenBoardTakeOn — "Take on" from the Open Board, using the SAME picks modal as a friend accept.
 *
 * There is no bespoke take-on screen on purpose: `AcceptChallengePicksModal` is already the app's
 * model for "here are the games, make your calls, confirm". The only thing an open challenge adds
 * is that the viewer is not a participant yet, which the modal handles via `takeOnOpen`.
 *
 * This component exists to feed that modal, because a board card carries neither the challenge's
 * markets nor its fixtures: it fetches the challenge (whose picks arrive sealed, with the
 * value-less `requiredMarkets` standing in) and the matches by id.
 *
 * specs/open-challenges AC-7/AC-7a.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { AcceptChallengePicksModal } from '../AcceptChallengePicksModal';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useChallengeDetailQuery } from '../../../src/challenges/hooks/useChallengeDetailQuery';
import { fetchMatches } from '../../../src/friends/helpers/friendsApi';
import { useEconomyStore } from '../../../src/shared/stores/economyStore';
import { useSnackbarStore } from '../../../src/shared/stores/snackbarStore';
import { useTheme } from '../../../src/theme/useTheme';
import { getOpenBoardTakeOnStyles } from './OpenBoardTakeOn.styles';

interface OpenBoardTakeOnProps {
  /** Challenge being taken on; `null` closes the flow. */
  challengeId: string | null;
  currentUserId: string;
  onClose: () => void;
  /** Fired after a successful take-on so the board can drop the claimed card. */
  onTakenOn: () => void;
}

export const OpenBoardTakeOn: React.FC<OpenBoardTakeOnProps> = ({
  challengeId,
  currentUserId,
  onClose,
  onTakenOn,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getOpenBoardTakeOnStyles(theme), [theme]);
  const apiClient = useApiClient();
  const showSnackbar = useSnackbarStore((s) => s.showSnackbar);
  const coins = useEconomyStore((s) => s.coins);
  // Latch the modal open only once we have something to render, so it never flashes empty.
  const [ready, setReady] = useState(false);

  const { data: challenge, isError } = useChallengeDetailQuery(challengeId ?? undefined);

  const matchIds = useMemo(
    () => (challenge?.matchIds ?? []).filter(Boolean),
    [challenge?.matchIds]
  );

  const { data: matches } = useQuery({
    queryKey: ['challenges', 'take-on-matches', challengeId ?? '', matchIds.join(',')],
    queryFn: () => fetchMatches(apiClient, matchIds),
    enabled: challengeId != null && matchIds.length > 0,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (challengeId == null) {
      setReady(false);
      return;
    }
    if (challenge && (matches?.length ?? 0) > 0) setReady(true);
  }, [challengeId, challenge, matches]);

  useEffect(() => {
    if (!isError || challengeId == null) return;
    showSnackbar("Couldn't load that challenge. Please try again.", 'error');
    onClose();
  }, [isError, challengeId, showSnackbar, onClose]);

  if (challengeId == null) return null;

  if (!ready || !challenge) {
    return (
      <View style={styles.loading} testID="open-board-take-on-loading" pointerEvents="none">
        <ActivityIndicator color={theme.primary} />
      </View>
    );
  }

  const stake = challenge.stakeCoins ?? 0;
  // Earliest kickoff across the fetched fixtures. Taken from the matches rather than the
  // challenge's own date field because these are the same rows the server locks against.
  const firstGameDate = (matches ?? [])
    .map((m) => m.date)
    .filter(Boolean)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null;

  return (
    <AcceptChallengePicksModal
      visible
      challenge={challenge}
      currentUserId={currentUserId}
      matches={matches ?? []}
      firstGameDate={firstGameDate}
      // A taker always pays on confirm, so the balance check is unconditional here — unlike a
      // friend challenge, there is no "already staked" case to exempt.
      insufficientCoins={stake > 0 && coins < stake}
      takeOnOpen
      onShowMessage={({ message, variant }) => showSnackbar(message, variant)}
      onSuccess={onTakenOn}
      onClose={onClose}
    />
  );
};
